import React, { useState, useCallback, useMemo } from 'react';
import { ArrowLeft, Sparkles, Download, ShoppingCart, Palette, Square, Smartphone, Monitor, Wand2, Loader2 } from 'lucide-react';

// Move WatermarkedImage OUTSIDE the main component to prevent recreation
const WatermarkedImage = React.memo(({ src, alt, onError }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset states when src changes
  React.useEffect(() => {
    if (src) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [src]);

  return (
    <div className="relative group">
      {!imageLoaded && !imageError && src && (
        <div className="w-full aspect-square bg-gradient-to-br from-purple-900/50 to-blue-900/50 rounded-xl flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-white/60 text-sm">Loading image...</p>
          </div>
        </div>
      )}
      
      <img 
        src={src} 
        alt={alt}
        className={`w-full h-auto rounded-xl shadow-2xl transition-opacity duration-300 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onError={(e) => {
          console.error('Image failed to load:', src);
          setImageError(true);
          if (onError) onError('Failed to load generated image. The server may still be processing it.');
        }}
        onLoad={() => {
          console.log('✅ Image loaded successfully:', src);
          setImageLoaded(true);
          setImageError(false);
        }}
        crossOrigin="anonymous"
      />
      
      {imageError && (
        <div className="w-full aspect-square bg-red-900/20 border border-red-500/30 rounded-xl flex items-center justify-center">
          <div className="text-center p-4">
            <div className="text-red-400 text-4xl mb-2">⚠️</div>
            <p className="text-red-200 text-sm">Failed to load image</p>
            <button 
              onClick={() => {
                setImageError(false);
                setImageLoaded(false);
              }}
              className="mt-2 text-xs text-red-300 hover:text-red-100 underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

WatermarkedImage.displayName = 'WatermarkedImage';

const ImageCreator = ({ onBack }) => {
  const [description, setDescription] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('Impressionist');
  const [customStyle, setCustomStyle] = useState('');
  const [orientation, setOrientation] = useState('square');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  // Memoized painting styles to prevent re-creation on every render
  const paintingStyles = useMemo(() => [
    'Impressionist', 'Realism', 'Watercolor', 'Cyberpunk', 
    'Oil on Canvas', 'Charcoal', 'Surrealism', 'Pop Art', 
    'Art Nouveau', 'Cartoon', 'Custom'
  ], []);

  // Memoized orientations to prevent re-creation on every render
  const orientations = useMemo(() => [
    { id: 'square', label: 'Square', icon: Square, ratio: '1:1', size: '1024x1024' },
    { id: 'portrait', label: 'Portrait', icon: Smartphone, ratio: '3:4', size: '1024x1792' },
    { id: 'landscape', label: 'Landscape', icon: Monitor, ratio: '16:9', size: '1792x1024' }
  ], []);

  // Memoize title generation to prevent unnecessary recalculations
  const generatedTitle = useMemo(() => {
    if (!description.trim()) return '';
    return description.substring(0, 15) + (description.length > 15 ? '...' : '');
  }, [description]);

  // Memoize generate button state to prevent unnecessary re-renders  
  const isGenerateDisabled = useMemo(() => {
    return isGenerating || 
           !description.trim() || 
           !selectedStyle || 
           (selectedStyle === 'Custom' && !customStyle.trim());
  }, [isGenerating, description, selectedStyle, customStyle]);

  // Memoized description change handler
  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
  }, []);

  // Memoized style change handler
  const handleStyleChange = useCallback((style) => {
    setSelectedStyle(style);
  }, []);

  // Memoized custom style change handler
  const handleCustomStyleChange = useCallback((e) => {
    setCustomStyle(e.target.value);
  }, []);

  // Memoized orientation change handler
  const handleOrientationChange = useCallback((newOrientation) => {
    setOrientation(newOrientation);
  }, []);

  // Test backend connection
  const testBackend = useCallback(async () => {
    try {
      console.log('🔍 Testing backend connection...');
      setDebugInfo('Testing backend connection...');
      
      const response = await fetch('/api/images/health', {
        method: 'GET',
        credentials: 'include'
      });
      
      console.log('Backend response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend health check:', data);
        setDebugInfo(`✅ Backend connected on port ${data.port}`);
      } else {
        console.log('❌ Backend health check failed:', response.status);
        setDebugInfo(`❌ Backend returned status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Backend connection failed:', error);
      setDebugInfo(`❌ Backend connection failed: ${error.message}`);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!description.trim() || !selectedStyle) {
      setError('Please enter a description and select a painting style');
      return;
    }

    if (selectedStyle === 'Custom' && !customStyle.trim()) {
      setError('Please enter a custom style description');
      return;
    }

    setIsGenerating(true);
    setError('');
    setDebugInfo('');
    
    try {
      const styleToUse = selectedStyle === 'Custom' ? customStyle : selectedStyle;
      const currentSize = orientations.find(o => o.id === orientation)?.size || '1024x1024';
      
      const requestBody = {
        prompt: description,
        description: description,
        style: selectedStyle,
        customStyle: selectedStyle === 'Custom' ? customStyle : undefined,
        orientation: orientation,
        size: currentSize
      };
      
      console.log('🎨 Sending request to backend:', requestBody);
      setDebugInfo('Sending request to AI...');
      
      const response = await fetch('/api/images/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      console.log('📡 Backend response status:', response.status);
      console.log('📡 Backend response headers:', [...response.headers.entries()]);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend returned error:', response.status, errorText);
        setError(`Backend error (${response.status}): ${errorText}`);
        setDebugInfo(`Backend returned status ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log('📡 Backend response data:', data);
      
      if (data.success) {
        console.log('✅ Image generation successful');
        setGeneratedImage({
          url: data.imageUrl,
          style: styleToUse,
          orientation: orientation,
          title: generatedTitle,
          id: data.imageId,
          generationTime: data.generationTime
        });
        setDebugInfo('✅ Image generated successfully!');
      } else {
        console.error('❌ API returned success=false:', data);
        setError(data.error || 'Unknown API error');
        setDebugInfo(`API error: ${data.error || 'Unknown'}`);
      }
    } catch (error) {
      console.error('❌ Request failed:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // More specific error handling
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error: Cannot connect to backend server. Make sure it\'s running on port 8080.');
        setDebugInfo('❌ Network connection failed');
      } else if (error.name === 'SyntaxError') {
        setError('Backend returned invalid response format.');
        setDebugInfo('❌ Invalid JSON response from backend');
      } else {
        setError(`Request error: ${error.message}`);
        setDebugInfo(`❌ ${error.name}: ${error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [description, selectedStyle, customStyle, orientation, orientations, generatedTitle]);

  const handleDownload = useCallback(async () => {
    if (!generatedImage?.id) return;
    
    try {
      const response = await fetch(`/api/images/${generatedImage.id}/download`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Create download link
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = data.filename || 'artwork.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Download failed: ' + data.error);
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed. Please try again.');
    }
  }, [generatedImage?.id]);

  const handleOrder = useCallback(() => {
    if (!generatedImage?.id) return;
    
    // Navigate to order page (implement this based on your routing)
    console.log('Navigate to order page for image:', generatedImage.id);
    alert('Order functionality will be implemented next!');
  }, [generatedImage?.id]);

  // Callback for handling image errors
  const handleImageError = useCallback((errorMessage) => {
    setError(errorMessage);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <nav className="px-8 py-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 text-white/80 hover:text-white transition-colors duration-200 hover:bg-white/10 rounded-lg"
              >
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center space-x-2">
                <Palette className="text-purple-400" size={28} />
                <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  canvasGen.ai
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={testBackend}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
              >
                Test Backend
              </button>
              <h1 className="text-2xl font-bold text-white">Create from Description</h1>
            </div>
          </div>
        </nav>

        <div className="px-8 pb-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Left Side - Controls */}
            <div className="space-y-8">
              {/* Debug Info Display */}
              {debugInfo && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-xl p-4">
                  <p className="text-blue-200 text-sm font-mono">{debugInfo}</p>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              {/* Description Input */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h2 className="text-2xl font-bold text-white mb-4">Describe Your Vision</h2>
                <textarea
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="A majestic sunset over snow-capped mountains with a peaceful lake in the foreground..."
                  className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-white/60 text-sm">
                    {description.length}/500 characters
                  </span>
                  <span className="text-white/60 text-sm">
                    Title: "{generatedTitle || 'Enter description...'}"
                  </span>
                </div>
              </div>

              {/* Orientation Selection */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">Choose Orientation</h3>
                <div className="grid grid-cols-3 gap-4">
                  {orientations.map((orient) => {
                    const IconComponent = orient.icon;
                    return (
                      <button
                        key={orient.id}
                        onClick={() => handleOrientationChange(orient.id)}
                        className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center space-y-2 ${
                          orientation === orient.id
                            ? 'border-purple-500 bg-purple-500/20 text-white'
                            : 'border-white/20 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <IconComponent size={24} />
                        <div className="text-center">
                          <div className="font-medium">{orient.label}</div>
                          <div className="text-xs opacity-70">{orient.ratio}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Style Selection */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">Select Painting Style</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {paintingStyles.map((style) => (
                    <button
                      key={style}
                      onClick={() => handleStyleChange(style)}
                      className={`p-3 rounded-lg border transition-all duration-200 text-sm font-medium ${
                        selectedStyle === style
                          ? 'border-purple-500 bg-purple-500/20 text-white'
                          : 'border-white/20 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>

                {/* Custom Style Input */}
                {selectedStyle === 'Custom' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      value={customStyle}
                      onChange={handleCustomStyleChange}
                      placeholder="Enter your custom style (e.g., 'Van Gogh inspired with swirling brushstrokes')"
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerateDisabled}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 shadow-lg flex items-center justify-center space-x-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Wand2 size={20} />
                    <span>Generate Artwork</span>
                  </>
                )}
              </button>
            </div>

            {/* Right Side - Preview */}
            <div className="space-y-6">
              {isGenerating ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">Creating Your Masterpiece...</h3>
                  
                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/50 to-blue-900/50 aspect-square flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl animate-bounce mb-4">🎨</div>
                      <h4 className="text-2xl font-bold text-white animate-pulse mb-2">
                        I'm working on it...
                      </h4>
                      <p className="text-white/80 animate-pulse">
                        Creating your {selectedStyle === 'Custom' ? customStyle : selectedStyle} masterpiece
                      </p>
                      
                      {/* Progress Dots */}
                      <div className="flex justify-center space-x-1 mt-4">
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-200 text-sm text-center">
                      <Wand2 size={16} className="inline mr-1" />
                      AI is analyzing your description and applying {selectedStyle === 'Custom' ? 'custom' : selectedStyle} style...
                    </p>
                  </div>
                </div>
              ) : generatedImage ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-4">Your Generated Artwork</h3>
                  
                  <WatermarkedImage 
                    key={generatedImage.id}
                    src={generatedImage.url} 
                    alt={generatedImage.title}
                    onError={handleImageError}
                  />

                  {/* Image Info */}
                  <div className="mt-4 space-y-2 text-white/80">
                    <div><strong>Title:</strong> {generatedImage.title}</div>
                    <div><strong>Style:</strong> {generatedImage.style}</div>
                    <div><strong>Orientation:</strong> {generatedImage.orientation}</div>
                    {generatedImage.generationTime && (
                      <div><strong>Generation time:</strong> {(generatedImage.generationTime / 1000).toFixed(1)}s</div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={handleDownload}
                      className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <Download size={18} />
                      <span>Download</span>
                    </button>
                    <button
                      onClick={handleOrder}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <ShoppingCart size={18} />
                      <span>Order Print</span>
                    </button>
                  </div>

                  {/* Watermark Notice */}
                  <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-sm">
                      <Sparkles size={16} className="inline mr-1" />
                      Watermark will be removed when you order or download your artwork
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12 border border-white/20 border-dashed text-center">
                  <Palette size={48} className="mx-auto text-white/40 mb-4" />
                  <h3 className="text-xl font-bold text-white/60 mb-2">Your Artwork Will Appear Here</h3>
                  <p className="text-white/40">
                    Fill in the description and style, then click "Generate Artwork" to create your masterpiece
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCreator;